import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, KeyRound, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { API_BASE } from '../../config/api';
import { formatApiErrorDetail } from '../../lib/apiError';

const API = `${API_BASE}/api`;

export default function EditUserDialog({ user, open, onOpenChange, token, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [profile, setProfile] = useState({ full_name: '', email: '' });
  const [pw, setPw] = useState('');
  const [requireChange, setRequireChange] = useState(true);
  const [busy, setBusy] = useState('');
  const [link, setLink] = useState('');

  useEffect(() => {
    if (open) { setProfile({ full_name: user.full_name || '', email: user.email || '' }); setPw(''); setRequireChange(true); setLink(''); }
  }, [open, user]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy('');
  };
  const changed = profile.full_name.trim() !== (user.full_name || '') || profile.email.trim().toLowerCase() !== (user.email || '').toLowerCase();
  const saveProfile = () => run('profile', async () => {
    await axios.put(`${API}/admin/users/${user.user_id}`, { full_name: profile.full_name.trim(), email: profile.email.trim() }, { headers });
    toast.success('Profile updated');
    onSaved?.();
  });
  const setPassword = () => run('password', async () => {
    await axios.post(`${API}/admin/users/${user.user_id}/reset-password`, { new_password: pw, require_change: requireChange }, { headers });
    toast.success(requireChange ? 'Password set. They\'ll be asked to change it when they next sign in.' : 'Password set');
    setPw('');
  });
  const emailLink = () => run('link', async () => {
    const { data } = await axios.post(`${API}/admin/users/${user.user_id}/send-invite`, {}, { headers });
    if (data.invite_sent) toast.success(`Reset link emailed to ${user.email}`);
    else { setLink(data.link); toast.message('Email isn\'t working right now. Copy the link below and share it.'); }
  });

  const section = 'rounded-lg border border-zinc-800 bg-zinc-950/50 p-4 space-y-3';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription className="text-zinc-400">Change {user.full_name || user.email}'s name, email or password.</DialogDescription>
        </DialogHeader>
        <div className={section}>
          <label className="block text-xs text-zinc-400">Full name
            <Input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} className="mt-1 bg-zinc-950 border-zinc-700" data-testid="edit-user-name-input" />
          </label>
          <label className="block text-xs text-zinc-400">Email (also their sign-in)
            <Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="mt-1 bg-zinc-950 border-zinc-700" data-testid="edit-user-email-input" />
          </label>
          <div className="flex justify-end">
            <Button onClick={saveProfile} disabled={!changed || !profile.email.trim() || busy === 'profile'} className="bg-teal-500 hover:bg-teal-400 text-black" data-testid="edit-user-save-btn">
              <Save className="h-4 w-4 mr-1.5" />{busy === 'profile' ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </div>
        <div className={section}>
          <p className="text-sm font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-navigate-gold" />Password</p>
          <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 8 characters)" className="bg-zinc-950 border-zinc-700" data-testid="edit-user-password-input" />
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" checked={requireChange} onChange={(e) => setRequireChange(e.target.checked)} data-testid="edit-user-require-change" />
            Ask them to change it when they next sign in
          </label>
          <div className="flex flex-wrap justify-between gap-2">
            <Button variant="outline" onClick={emailLink} disabled={busy === 'link'} className="border-zinc-700 text-zinc-200 hover:bg-zinc-800" data-testid="edit-user-email-link-btn">
              <Mail className="h-4 w-4 mr-1.5" />{busy === 'link' ? 'Sending...' : 'Email a reset link'}
            </Button>
            <Button onClick={setPassword} disabled={pw.length < 8 || busy === 'password'} className="bg-teal-500 hover:bg-teal-400 text-black" data-testid="edit-user-set-password-btn">
              {busy === 'password' ? 'Setting...' : 'Set password'}
            </Button>
          </div>
          {link && <Input readOnly value={link} onFocus={(e) => e.target.select()} className="bg-zinc-950 border-zinc-700 text-xs" data-testid="edit-user-reset-link" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
